import React from "react";

import SectionHeading from "components/SectionHeading";
import { emissionFactorsNIR, emissionFactorsPCF2004 } from "utils/emissions/getEmissionFactors";

import sharedClasses from "../style.module.scss";
import classes from "./style.module.scss";

const REGION_NAMES = {
    NL: "Newfoundland and Labrador",
    NS: "Nova Scotia",
    PE: "Prince Edward Island",
    NB: "New Brunswick",
    QC: "Quebec",
    ON: "Ontario",
    MB: "Manitoba",
    SK: "Saskatchewan",
    AB: "Alberta",
    BC: "British Columbia",
    YT: "Yukon",
    NT: "Northwest Territories",
    NU: "Nunavut",
};

const NIR_REGION_ORDER = Object.keys(emissionFactorsNIR);
const NBC_2025_REGION_ORDER = Object.keys(emissionFactorsPCF2004);

const NirReferences = () => (
    <ul className={classes.references}>
        <li>Last updated with 2026 National Inventory Report (NIR).</li>
        <li>Electricity: g/kWh, generation intensity (NIR Part 3).</li>
        <li>Natural gas: g/m³ (NIR Part 2, Table A6.1-1).</li>
        <li>Propane: g/L (NIR Part 2, Table A6.1-4).</li>
        <li>Oil (light fuel oil, residential): g/L (NIR Part 2, Table A6.1-5).</li>
        <li>Wood: g/kg fuel (NIR Part 2, Table A6.6-1).</li>
    </ul>
);

const Nbc2025References = () => (
    <p className={classes.referencesNote}>
        NBC 2025 greenhouse gas emission factors (Table 9.36.15.5.-A) as implemented in Volta SNAP. Units match the NIR
        table above: electricity in g/kWh, natural gas in g/m³, propane and oil in g/L.
    </p>
);

export default () => (
    <div>
        <SectionHeading
            title="Emission Factors"
            subtitle="Regional emission factors used for greenhouse gas calculations in Volta SNAP."
        />
        <div className={sharedClasses.contentWrapper}>
            <section className={classes.section}>
                <h3>National Inventory Report (NIR) emission factors</h3>
                <p>
                    Values from Canada&apos;s National Inventory Report (NIR), by province and territory. These are used
                    for all emission calculations in Volta SNAP that are not related to NBC 2025 compliance.
                </p>
                <div className={classes.tableWrapper}>
                    <table className={classes.factorTable}>
                        <thead>
                            <tr>
                                <th>Region</th>
                                <th>Electricity (g/kWh)</th>
                                <th>Natural gas (g/m³)</th>
                                <th>Propane (g/L)</th>
                                <th>Oil (g/L)</th>
                                <th>Wood stove (g/kg fuel)</th>
                            </tr>
                        </thead>
                        <tbody>
                            {NIR_REGION_ORDER.map((code) => {
                                const factors = emissionFactorsNIR[code];
                                return (
                                    <tr key={code}>
                                        <td>{REGION_NAMES[code]}</td>
                                        <td>{factors.elec}</td>
                                        <td>{factors.ng}</td>
                                        <td>{factors.propane}</td>
                                        <td>{factors.oil}</td>
                                        <td>{factors.woodStove}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                <NirReferences />
            </section>

            <section className={classes.section}>
                <h3>NBC 2025 emission factors (9.36.15)</h3>
                <p>
                    Provincial and territorial emission factors used for NBC 2025 compliance calculations in Volta SNAP.
                </p>
                <div className={classes.tableWrapper}>
                    <table className={classes.factorTable}>
                        <thead>
                            <tr>
                                <th>Region</th>
                                <th>Electricity (g/kWh)</th>
                                <th>Natural gas (g/m³)</th>
                                <th>Propane (g/L)</th>
                                <th>Oil (g/L)</th>
                            </tr>
                        </thead>
                        <tbody>
                            {NBC_2025_REGION_ORDER.map((code) => {
                                const factors = emissionFactorsPCF2004[code];
                                return (
                                    <tr key={code}>
                                        <td>{REGION_NAMES[code]}</td>
                                        <td>{factors.elec}</td>
                                        <td>{factors.ng}</td>
                                        <td>{factors.prop}</td>
                                        <td>{factors.oil}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                <Nbc2025References />
            </section>
        </div>
    </div>
);
